import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { inQuietHours } from '@nexus/core';
import {
  completeFromNotification,
  runReminderCycle,
  snoozeNotification,
  type CycleText,
} from '@nexus/workspace';
import type { NotificationRecord, Store } from '@nexus/store';
import { isTauri } from '@nexus/store/tauri';
import { useStore } from '../db/context';
import { formatDue } from '../components/tasks/format';
import { useUi } from '../state/ui';
import { loadNotificationSettings } from './settings';
import { onNotificationAction, onPushReminderClick, showSystemNotification } from './system';
import { useToasts } from './toasts';

const INTERVAL_MS = 30_000;
const TASK_CHANGE_DELAY_MS = 2_000;

export type NotificationActionKind = 'open' | 'done' | 'snooze';

/** Texts of reminders and summaries in the current language. */
export function cycleText(t: TFunction, language: string): CycleText {
  return {
    reminderBody: (task) =>
      [
        task.due ? formatDue(task.due, language, t, undefined, task.dueTime) : null,
        task.sourceTitle ? t('notifications.inPage', { title: task.sourceTitle }) : null,
      ]
        .filter(Boolean)
        .join(' · '),
    memoBody: () => t('memos.notificationBody'),
    dailyTitle: (c) => t('notifications.daily', { today: c.today, overdue: c.overdue }),
    weeklyTitle: (c) => t('notifications.weekly', { nextWeek: c.nextWeek, overdue: c.overdue }),
  };
}

/** Open a reminder's page and point at the task (also for pushed reminders). */
export function openReminder(pageId: string, taskId?: string | null) {
  useUi.getState().open({ kind: 'page', pageId });
  if (taskId) useUi.getState().set({ focusTask: { pageId, taskId } });
}

/** Open the page of a notification and point at the task. */
export function openNotification(store: Store, n: NotificationRecord) {
  // Close the bell popover so the opened page or view is actually visible.
  useUi.getState().set({ notificationsOpen: false });
  if (n.pageId) openReminder(n.pageId, n.taskId);
  else if (n.kind === 'summary') useUi.getState().open({ kind: 'tasks', tab: 'today' });
  else useUi.getState().set({ notificationsOpen: true });
  void store.markNotificationsRead([n.id]);
}

export async function handleNotificationAction(
  store: Store,
  n: NotificationRecord,
  action: NotificationActionKind,
) {
  useToasts.getState().dismiss(n.id);
  if (action === 'open') return openNotification(store, n);
  if (action === 'done') return completeFromNotification(store, n);
  const settings = await loadNotificationSettings(store);
  await snoozeNotification(store, n, { minutes: settings.defaultSnoozeMinutes }, settings);
}

/**
 * Runs the reminder cycle while the app is open (also when the desktop window is
 * hidden in the tray): every 30 s, when tasks change and when the app comes back.
 */
export function useReminderScheduler() {
  const store = useStore();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    let running = false;
    let disposed = false;
    const text = cycleText(t, i18n.language);

    let again = false;
    const cycle = async () => {
      if (disposed) return;
      // A tick during a running cycle is not lost: run once more right after.
      if (running) {
        again = true;
        return;
      }
      running = true;
      try {
        const settings = await loadNotificationSettings(store);
        const delivered = await runReminderCycle(store, settings, text);
        const missed = delivered.filter((d) => d.notification.kind === 'missed');
        for (const d of delivered.filter((x) => x.announce)) {
          useToasts.getState().push(d.notification);
          if (settings.system) {
            void showSystemNotification(
              {
                id: d.notification.id,
                tag: d.notification.key,
                title: d.notification.title,
                body: d.notification.body,
                ...(d.notification.taskId
                  ? {
                      actions: {
                        done: t('notifications.done'),
                        snooze: t('notifications.snoozeDefault', {
                          minutes: settings.defaultSnoozeMinutes,
                        }),
                      },
                    }
                  : {}),
              },
              () => openNotification(store, d.notification),
            );
          }
        }
        if (missed.length && !inQuietHours(Date.now(), settings)) {
          const title = t('notifications.missedCount', { count: missed.length });
          useToasts.getState().push({
            ...missed[0]!.notification,
            id: `missed-${Date.now()}`,
            kind: 'missed',
            title,
            body: '',
            taskId: null,
            pageId: null,
          });
          if (settings.system) {
            void showSystemNotification({ id: `missed-${Date.now()}`, title, body: '' }, () =>
              useUi.getState().set({ notificationsOpen: true }),
            );
          }
        }
      } finally {
        running = false;
        if (again && !disposed) {
          again = false;
          void cycle();
        }
      }
    };

    void store.pruneNotifications().then(cycle);
    const interval = setInterval(() => void cycle(), INTERVAL_MS);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const offTasks = store.subscribe(() => {
      clearTimeout(timer);
      timer = setTimeout(() => void cycle(), TASK_CHANGE_DELAY_MS);
    }, ['tasks']);
    const onVisible = () => document.visibilityState === 'visible' && void cycle();
    document.addEventListener('visibilitychange', onVisible);

    // Buttons on browser notifications (via the service worker).
    const offActions = onNotificationAction(async ({ id, action }) => {
      const n = (await store.listNotifications(500)).find((x) => x.id === id);
      if (n) await handleNotificationAction(store, n, action);
      else if (id?.startsWith('missed-')) useUi.getState().set({ notificationsOpen: true });
    });
    const offPush = onPushReminderClick(({ pageId, taskId }) => openReminder(pageId, taskId));

    // Desktop tray: "Snel een taak toevoegen".
    let offTray = () => {};
    if (isTauri()) {
      void import('@tauri-apps/api/event').then(({ listen }) =>
        listen('nexus://capture', () => useUi.getState().set({ captureOpen: true })).then((un) => {
          if (disposed) un();
          else offTray = un;
        }),
      );
    }

    return () => {
      disposed = true;
      clearInterval(interval);
      clearTimeout(timer);
      offTasks();
      offActions();
      offPush();
      offTray();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [store, t, i18n.language]);
}
